/**
 * Quotation Tracking Service for Netlizing
 *
 * This service handles tracking successful quotations via dataLayer.
 * It includes deduplication logic to prevent duplicate events on page refresh.
 */

import { storeLocalValue, getLocalValue } from "../utils/tracking";
import { formatPhoneToInternational } from "../utils/phoneFormatter";

// Constants
const STORAGE_PREFIX = "nl_";
const DEDUP_KEY = "quotation_tracked";
const DEBUG_KEY = "quotation_debug";

/**
 * Check if we're in a development environment
 *
 * @returns {boolean} True if in dev or local environment
 */
function isDevEnvironment() {
    const hostname = window.location.hostname;
    return (
        hostname === "localhost" ||
        hostname.includes("127.0.0.1") ||
        hostname.includes("dev.netlizing.hu")
    );
}

/**
 * Log dataLayer events in development environments
 *
 * @param {string} message - Message to log
 * @param {any} data - Data to log
 */
function devLog(message, data = null) {
    if (isDevEnvironment()) {
        if (data) {
            console.log(`[Quotation Tracking] ${message}`, data);
        } else {
            console.log(`[Quotation Tracking] ${message}`);
        }
    }
}

/**
 * Store a dataLayer event in the debug history
 * This persists across page reloads
 *
 * @param {Object} event - The dataLayer event to store
 */
function storeDebugEvent(event) {
    if (!isDevEnvironment()) return;

    try {
        // Get existing debug history
        let history = [];
        const existing = localStorage.getItem(STORAGE_PREFIX + DEBUG_KEY);

        if (existing) {
            history = JSON.parse(existing);
            if (!Array.isArray(history)) {
                history = [];
            }
        }

        // Add timestamp to event
        const eventWithTimestamp = {
            ...event,
            _debug_timestamp: new Date().toISOString(),
            _debug_page: window.location.href
        };

        // Add to history (limit to last 20 events)
        history.push(eventWithTimestamp);
        if (history.length > 20) {
            history = history.slice(history.length - 20);
        }

        // Store back to localStorage
        localStorage.setItem(
            STORAGE_PREFIX + DEBUG_KEY,
            JSON.stringify(history)
        );
    } catch (e) {
        console.error("[Quotation Tracking] Error storing debug event:", e);
    }
}

/**
 * Track a successful quotation by pushing data to the dataLayer
 * Includes deduplication logic to prevent duplicate events
 *
 * @param {Object} quotationData - The quotation data to track
 * @returns {Promise<boolean>} - Promise that resolves with whether the tracking was performed (false if deduplicated)
 */
export async function trackQuotation(quotationData) {
    // Check if this quotation has already been tracked in this session
    const quotationId = quotationData.quotation_id;

    if (!quotationId) {
        devLog("No quotation_id provided for tracking");
        return false;
    }

    const trackedQuotations = getLocalValue(DEDUP_KEY, STORAGE_PREFIX);

    // If we have tracked quotations, check if this one is already tracked
    if (trackedQuotations) {
        try {
            const tracked = JSON.parse(trackedQuotations);
            if (Array.isArray(tracked) && tracked.includes(quotationId)) {
                devLog(`Quotation ${quotationId} already tracked, skipping`);
                return false;
            }
        } catch (e) {
            // If parsing fails, proceed with tracking
            console.error("Error parsing tracked quotations:", e);
        }
    }

    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Format phone number to international format
    const formattedPhone = formatPhoneToInternational(quotationData.phone);

    // Create the event object
    const eventObject = {
        event: "quotation",
        quotation_id: quotationData.quotation_id || "",
        email: quotationData.email || "",
        phone: formattedPhone,
        first_name: quotationData.first_name || "",
        last_name: quotationData.last_name || "",
        company: quotationData.company || ""
    };

    // Push the quotation data to dataLayer
    window.dataLayer.push(eventObject);

    // Store event in debug history (persists across page reloads)
    storeDebugEvent(eventObject);

    // Store this quotation ID to prevent duplicate tracking
    storeTrackedQuotation(quotationId);

    // Return a promise that resolves after a small delay
    // to ensure dataLayer has time to process
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(true);
        }, 100);
    });
}

/**
 * Store a quotation ID as tracked to prevent duplicate tracking
 *
 * @param {string} quotationId - The quotation ID to mark as tracked
 */
function storeTrackedQuotation(quotationId) {
    if (!quotationId) return;

    let tracked = [];
    const existing = getLocalValue(DEDUP_KEY, STORAGE_PREFIX);

    if (existing) {
        try {
            tracked = JSON.parse(existing);
            if (!Array.isArray(tracked)) {
                tracked = [];
            }
        } catch (e) {
            tracked = [];
        }
    }

    // Add the new quotation ID if it's not already tracked
    if (!tracked.includes(quotationId)) {
        tracked.push(quotationId);

        // Store back to localStorage
        storeLocalValue(DEDUP_KEY, JSON.stringify(tracked), STORAGE_PREFIX);
    }
}
